/**
 * The four activities, named and illustrated in one place.
 *
 * The Learning Journey, every test screen, the answer review and the snapshot
 * letter all read from here, so a child sees the same name and the same icon
 * on the journey card and inside the test itself.
 */

export type TestKey = 'spelling' | 'speaking' | 'reading' | 'logic';

export interface TestIdentity {
  key: TestKey;
  /** The name parents and children see everywhere. */
  title: string;
  desc: string;
  /** Journey card artwork, served from `public/assets/journey`. */
  logo: string;
  /** Shown if the artwork fails to load. */
  icon: string;
  meta: string;
}

export const TEST_ORDER: TestKey[] = ['spelling', 'speaking', 'reading', 'logic'];

export const TESTS: Record<TestKey, TestIdentity> = {
  spelling: {
    key: 'spelling',
    title: 'Word Wizard',
    desc: 'Play, improve, and explore.',
    logo: '/assets/journey/word-wizard.png',
    icon: '🔤',
    meta: '5 min · 10 questions',
  },
  speaking: {
    key: 'speaking',
    title: 'Read Aloud',
    desc: 'Words in a voice become real.',
    logo: '/assets/journey/read-aloud.png',
    icon: '🎤',
    meta: '5 min · 9 questions',
  },
  reading: {
    key: 'reading',
    title: 'Story Explorer',
    desc: 'Understand the heart of every story.',
    logo: '/assets/journey/story-explorer.png',
    icon: '📖',
    meta: '6 min · 10 questions',
  },
  logic: {
    key: 'logic',
    title: 'Logic Quest',
    desc: 'Puzzles, solving and growing.',
    logo: '/assets/journey/logic-quest.png',
    icon: '⚙️',
    meta: '3 min · 8 questions',
  },
};

export const TEST_LIST: TestIdentity[] = TEST_ORDER.map((key) => TESTS[key]);

/** Keys the API uses for the same activities (`tests_completed`, result payloads). */
const API_ALIASES: Record<string, TestKey> = {
  comprehension: 'reading',
  voice: 'speaking',
  words: 'spelling',
  logical: 'logic',
};

/**
 * Names earlier copy gave these activities. The snapshot prompt still writes
 * some of them, so they are resolved back to the current name rather than
 * showing a child two names for the same test.
 */
const LEGACY_TITLES: Record<string, TestKey> = {
  'voice challenge': 'speaking',
  'speaking test': 'speaking',
  'reading room': 'reading',
  'reading test': 'reading',
  'spelling test': 'spelling',
  'logic test': 'logic',
};

const BY_TITLE: Record<string, TestKey> = TEST_ORDER.reduce(
  (map, key) => ({ ...map, [TESTS[key].title.toLowerCase()]: key }),
  {} as Record<string, TestKey>
);

export const toTestKey = (value?: string): TestKey | undefined => {
  const key = String(value ?? '').toLowerCase().trim();
  if (!key) return undefined;
  if (key in TESTS) return key as TestKey;
  return API_ALIASES[key] ?? BY_TITLE[key] ?? LEGACY_TITLES[key];
};

/** Swaps any legacy activity name inside a sentence for the name in use today. */
export const withCurrentTestNames = (text: string): string =>
  Object.entries(LEGACY_TITLES).reduce(
    (out, [legacy, key]) => out.replace(new RegExp(`\\b${legacy}\\b`, 'gi'), TESTS[key].title),
    text
  );

/** The display name for a key from either the app or the API; unknown keys pass through. */
export const testTitle = (value?: string): string => {
  const key = toTestKey(value);
  if (key) return TESTS[key].title;
  return String(value ?? '').replace(/^./, (c) => c.toUpperCase());
};
